import { createSelector } from '@ngrx/store';
import { IAppState } from '../store.dto';

const PathfinderUserRolesMasterList = ['None', 'Operator', 'Administrator'];

export const selectUserSession = (state: IAppState) => state.data.UserSession;

export const RolesMasterList = [
  'administrator',
  'scheduler',
  'job-editor',
  'job-editor-quantity',
  'pfpc',
  'operator',
  'tooling-editor',
  'pattern-editor',
  'machine-manager',
  'basic',
];
/**
 * The roles allowed to change each area. Mirrors EditRoles in AMS.Eclipse.Server
 * (Users/ProRolesManager.cs), which enforces them — change both together. The server adds 'basic'
 * to the session roles of anyone holding any other role.
 */
export const EditRoles = {
  administrator: ['administrator'],
  /**
   * Everyday actions: report and alert subscriptions, printing coil and bundle tags, acknowledging
   * alerts, messaging operators, running imports and retrying exports, contacting support.
   */
  basic: ['basic'],
  /** Shift times, print settings, loss codes, wallboards/andon, material details, warehouse settings. */
  plantSettings: ['administrator', 'machine-manager'],
  /**
   * XL200 machine configuration: shape, segment and tooling-code tables, part printing, orientation,
   * setup libraries, user fields, the machine's patterns, and Pathfinders.
   */
  machines: ['administrator', 'machine-manager'],
  /** The machine details save, shared with the schedule page's auto-push settings. */
  machineDetails: ['administrator', 'machine-manager', 'scheduler'],
  tooling: ['administrator', 'tooling-editor'],
  /** Punch patterns. Administrator alone isn't enough. */
  patterns: ['pattern-editor'],
  /** Assigning jobs to machines and removing them. */
  scheduling: ['scheduler'],
  /** Order changes that keep the total quantity (job-editor-quantity implies job-editor). */
  orders: ['job-editor'],
  /** Creating and duplicating orders, and adding or deleting items. */
  orderQuantities: ['job-editor-quantity'],
  orderHold: ['scheduler', 'job-editor'],
  /** Performance standards, changeover configuration and metric configuration. */
  performanceStandards: ['administrator', 'machine-manager', 'tooling-editor'],
  /** The site-wide shape geometry and shape alias tables. */
  shapes: ['administrator', 'pattern-editor'],
  plannedDowntime: ['administrator', 'scheduler'],
  /** Creating coils and moving them between locations. */
  inventory: ['administrator', 'machine-manager'],
} as const satisfies Record<string, readonly string[]>;

export const selectUsers = (state: IAppState) => state.data.collections.Users;
export const selectFolders = (state: IAppState) => state.data.collections.Folders;
const selectSystemPreferences = (state: IAppState) => state.data.collections.SystemPreferences;

export const UserHasRole = (role: string) =>
  createSelector(selectUserSession, (session) =>
    session ? session.roles.some((r) => r.toLowerCase() === role.toLowerCase()) : false
  );

export const UserHasRoles = (roles: readonly string[], allRoles: boolean) =>
  createSelector(selectUserSession, (session) => {
    if (!session) return false;

    const matchedRoles = session.roles.filter((r) =>
      roles.some((x) => x.toLowerCase() === r.toLowerCase())
    );

    return allRoles ? matchedRoles.length === roles.length : matchedRoles.length > 0;
  });

export const UserHasClaim = (claim: string) =>
  createSelector(selectUserSession, (session) =>
    session ? session.claims.some((c) => c.toLowerCase() === claim.toLowerCase()) : false
  );

export const selectUserIsAdmin = createSelector(
  (state: IAppState) => state,
  (state) => UserHasRole('administrator')(state)
);

export const selectUserListModel = createSelector(
  selectUsers,
  selectFolders,
  selectSystemPreferences,
  selectUserIsAdmin,
  (users, folders, systemPreferences, userIsAdmin) => ({
    users: users.map((u) => ({
      ...u,
      rolesModel: RolesMasterList.map((rm) => ({
        roleName: rm,
        enabled: u.roles.includes(rm), // More readable than `indexOf(rm) > -1`
      })),
      folderRolesModel: folders.reduce((pfroles, pf) => {
        pfroles[pf.id] = u.folderRoles.find((x) => x.folderId === pf.id)?.role || 'None';
        return pfroles;
      }, {} as Record<string, string>),
    })),
    systemPreferences,
    userIsAdmin,
    PathfinderUserRolesMasterList,
    pathfinders: folders,
  })
);
